<script>
  import { onMount } from 'svelte';
  import {
    Button,
    DataTable,
    InlineNotification,
    Modal,
    Pagination,
    TextInput,
    Toolbar,
    ToolbarContent,
    ToolbarSearch,
  } from 'carbon-components-svelte';
  import Add16 from 'carbon-icons-svelte/lib/Add16';
  import TrashCan16 from 'carbon-icons-svelte/lib/TrashCan16';

  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import RelativeDate from '../../components/RelativeDate.svelte';

  import { addCloudfrontEnabledBucket, getCloudfrontEnabledBuckets, removeCloudfrontEnabledBucket } from '../../services';
  import { getServerErrorMessage } from '../../utils';
  import { S3_BUCKET_NAME_OR_PREFIX_PATTERN } from '../../../shared/constants';

  const headers = [
    { key: 'bucket', value: 'Bucket' },
    { key: 'actions', empty: true, sort: false },
  ];

  let loading = true,
    errorMessage = null,
    config = { buckets: [], updatedAt: null, updatedByDisplayName: null },
    newBucket = '',
    newBucketError = null,
    // { type: 'add' | 'remove', bucket: string }
    pendingChange = null,
    submission = { loading: false, errorMessage: null },
    page = 1,
    pageSize = 25,
    filteredRowIds = [];

  async function fetchBuckets() {
    try {
      config = await getCloudfrontEnabledBuckets();
    } catch (error) {
      console.error('[AdminCloudfrontBuckets] Failed to load buckets!', error);
      errorMessage = getServerErrorMessage(error) ?? 'Failed to load CloudFront enabled buckets!';
    } finally {
      loading = false;
    }
  }

  function openConfirmation(type, bucket) {
    submission = { loading: false, errorMessage: null };
    pendingChange = { type, bucket };
  }

  function closeConfirmation() {
    pendingChange = null;
  }

  function requestAdd() {
    const bucket = newBucket.trim();
    newBucketError = !S3_BUCKET_NAME_OR_PREFIX_PATTERN.test(bucket)
      ? 'Use 2-63 lowercase letters, numbers, dots or hyphens, starting with a letter or number.'
      : buckets.includes(bucket)
      ? 'This bucket is already enabled.'
      : null;

    if (!newBucketError) {
      openConfirmation('add', bucket);
    }
  }

  async function confirmChange() {
    const { type, bucket } = pendingChange;
    submission = { loading: true, errorMessage: null };

    try {
      config = type === 'add' ? await addCloudfrontEnabledBucket(bucket) : await removeCloudfrontEnabledBucket(bucket);
      if (type === 'add') {
        newBucket = '';
      }

      submission = { loading: false, errorMessage: null };
      pendingChange = null;
    } catch (error) {
      console.error(`[AdminCloudfrontBuckets] Failed to ${type} bucket!`, error);
      submission = { loading: false, errorMessage: getServerErrorMessage(error) ?? `Failed to ${type} bucket! Please try again.` };
    }
  }

  onMount(fetchBuckets);

  $: ({ buckets } = config);
  $: rows = buckets.map((bucket) => ({ id: bucket, bucket }));
  $: isRemoval = pendingChange?.type === 'remove';
</script>

{#if errorMessage}
  <InlineNotification hideCloseButton kind="error" lowContrast title="Error:" subtitle={errorMessage} />
{:else}
  <div class="add-bucket">
    <TextInput
      size="sm"
      labelText="New bucket"
      placeholder="e.g. p-lseg-ll-feeds"
      bind:value={newBucket}
      invalid={!!newBucketError}
      invalidText={newBucketError}
      disabled={loading}
      on:input={() => (newBucketError = null)}
    />
    <Button size="small" icon={Add16} disabled={loading || !newBucket.trim()} on:click={requestAdd}>Add bucket</Button>
  </div>

  {#if config.updatedAt}
    <p class="last-update">
      Last updated <RelativeDate date={config.updatedAt} /> by {config.updatedByDisplayName}
    </p>
  {/if}

  <LoadingSpinner {loading}>
    <div class="table-container">
      <DataTable class="mst-table--lock-end" sortable {headers} {rows} {page} {pageSize}>
        <Toolbar>
          <ToolbarContent>
            <ToolbarSearch persistent shouldFilterRows placeholder="Search buckets..." bind:filteredRowIds />
          </ToolbarContent>
        </Toolbar>
        <svelte:fragment slot="cell" let:cell let:row>
          {#if cell.key === 'actions'}
            <Button
              kind="danger-ghost"
              size="small"
              icon={TrashCan16}
              iconDescription={buckets.length === 1 ? 'The last bucket cannot be removed' : 'Remove bucket'}
              tooltipPosition="left"
              disabled={buckets.length === 1}
              on:click={() => openConfirmation('remove', row.bucket)}
            />
          {:else}
            <span class="bucket-name">{cell.value}</span>
          {/if}
        </svelte:fragment>
      </DataTable>
    </div>
    <Pagination bind:page bind:pageSize pageSizes={[10, 25, 50]} totalItems={filteredRowIds.length} />
  </LoadingSpinner>
{/if}

<Modal
  danger={isRemoval}
  open={!!pendingChange}
  size="sm"
  preventCloseOnClickOutside
  modalHeading={isRemoval ? 'Remove CloudFront enabled bucket' : 'Add CloudFront enabled bucket'}
  primaryButtonText={isRemoval ? 'Remove' : 'Add'}
  secondaryButtonText="Cancel"
  primaryButtonDisabled={submission.loading}
  on:submit={confirmChange}
  on:close={closeConfirmation}
  on:click:button--secondary={closeConfirmation}
>
  <LoadingSpinner loading={submission.loading}>
    {#if submission.errorMessage}
      <InlineNotification hideCloseButton kind="error" lowContrast title="Error:" subtitle={submission.errorMessage} />
    {/if}
    {#if pendingChange}
      <p>
        You are about to {isRemoval ? 'remove' : 'add'} <strong>{pendingChange.bucket}</strong>
        {isRemoval ? 'from' : 'to'} the CloudFront enabled S3 buckets.
      </p>
      {#if !isRemoval && buckets.length === 0}
        <p class="warning">No buckets are configured yet, so this will restrict CloudFront access to this bucket only.</p>
      {/if}
      <p>This affects prefix permissions for all users. Are you sure?</p>
    {/if}
  </LoadingSpinner>
</Modal>

<style>
  .add-bucket {
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;
    margin-bottom: 1rem;
    max-width: 32rem;
  }

  p + p {
    margin-top: 1rem;
  }

  .last-update {
    color: var(--cds-text-02);
    font-size: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .bucket-name {
    white-space: nowrap;
  }

  .warning {
    color: var(--cds-text-error);
  }

  .table-container :global(td:last-child) {
    width: 50px;
  }
</style>
